import { useState, type FormEvent, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, KeyRound } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { useAuthStore } from "../../stores/authStore";

export default function Register() {
  const [step, setStep] = useState<"register" | "verify">("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    register,
    verifyEmail,
    isLoading,
    error,
    clearError,
    isAuthenticated,
  } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    clearError();
    setLocalError(null);
  }, [email, password, confirmPassword, fullName, otpCode, clearError]);

  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);

    if (password !== confirmPassword) {
      setLocalError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setLocalError("Password must be at least 8 characters");
      return;
    }

    try {
      const result = await register(email, password, fullName);
      setSuccessMessage(result.message);
      setStep("verify");
    } catch {
      /* error is set in store */
    }
  }

  async function handleVerify(e: FormEvent) {
    e.preventDefault();
    try {
      await verifyEmail(email, otpCode);
      navigate("/", { replace: true });
    } catch {
      /* error is set in store */
    }
  }

  const displayError = localError || error;

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-900 p-4">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 h-80 w-80 rounded-full bg-purple-500/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-80 w-80 rounded-full bg-brand-500/10 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="mb-8 flex flex-col items-center">
          <img
            src="/files/favicon-512.png"
            alt="Asset Radar logo"
            className="mb-4 h-14 w-14 rounded-2xl shadow-lg shadow-brand-500/30"
          />
          <h1 className="text-2xl font-bold text-surface-100">
            {step === "register" ? "Create Account" : "Verify Email"}
          </h1>
          <p className="mt-1 text-sm text-surface-400">
            {step === "register"
              ? "Get started with Asset Radar"
              : "Enter the verification code sent to your email"}
          </p>
        </div>

        <div className="rounded-2xl border border-surface-700/50 bg-surface-800/60 backdrop-blur-xl p-8 shadow-2xl shadow-black/20">
          {displayError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
            >
              {displayError}
            </motion.div>
          )}

          {successMessage && step === "verify" && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mb-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300"
            >
              {successMessage}
            </motion.div>
          )}

          {step === "register" ? (
            <form onSubmit={handleRegister} className="space-y-5">
              <Input
                label="Full Name"
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                icon={<User className="h-4 w-4" />}
                required
                autoComplete="name"
              />

              <Input
                label="Email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={<Mail className="h-4 w-4" />}
                required
                autoComplete="email"
              />

              <Input
                label="Password"
                type="password"
                placeholder="Minimum 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={<Lock className="h-4 w-4" />}
                required
                autoComplete="new-password"
              />

              <Input
                label="Confirm Password"
                type="password"
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                icon={<Lock className="h-4 w-4" />}
                required
                autoComplete="new-password"
              />

              <Button
                type="submit"
                isLoading={isLoading}
                className="w-full"
                size="lg"
              >
                Create Account
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-5">
              <Input
                label="Verification Code"
                type="text"
                placeholder="Enter 6-digit code"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                icon={<KeyRound className="h-4 w-4" />}
                required
                maxLength={6}
                autoComplete="one-time-code"
              />

              <Button
                type="submit"
                isLoading={isLoading}
                className="w-full"
                size="lg"
              >
                Verify & Sign In
              </Button>

              <button
                type="button"
                onClick={() => setStep("register")}
                className="w-full text-center text-sm text-surface-400 hover:text-surface-300 transition-colors"
              >
                Back to registration
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-surface-400">
          Already have an account?{" "}
          <Link
            to="/login"
            className="text-brand-400 hover:text-brand-300 font-medium transition-colors"
          >
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
